// src/pages/admin/JournalEntryPreview.jsx
import { useMemo } from "react";
import {
  PreviewField,
  PreviewInput,
  fmtLineAmount,
  computeLineBalance,
  useJournalEntryLineLookups,
} from "@common";

/**
 * Read-only summary shown in the confirmation modal before sending a
 * Journal Entry to NetSuite.
 *
 * Props:
 *   preview = { clinic, clinicRows, payload }
 *   payload = { memo, subsidiary:{id}, trandate, line:{items:[{account:{id}, debit|credit}]} }
 */
export default function JournalEntryPreview({ preview, onChange }) {
  const items = useMemo(() => preview?.payload?.line?.items ?? [], [preview]);
  const { accountNameById, subsidiaryNameById, accountsQuery, subsidiariesQuery } =
    useJournalEntryLineLookups(preview?.payload);

  const subsidiaryId = preview?.payload?.subsidiary?.id ?? null;
  const subsidiaryName = subsidiaryId != null ? subsidiaryNameById[String(subsidiaryId)] : "";

  if (!preview) return null;
  const { clinic, clinicRows, payload } = preview;

  const { totalDebit, totalCredit, balanced } = computeLineBalance(items);
  const accountsLoading = accountsQuery.isLoading;

  return (
    <div className="space-y-4">
      {/* Header fields */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 rounded-md border border-palette-gray-divider bg-palette-gray-soft p-3">
        <PreviewField label="Clinic" value={clinic} />
        <PreviewField
          label="Subsidiary"
          value={
            subsidiaryName
              ? subsidiaryName
              : subsidiariesQuery.isLoading
                ? "Loading\u2026"
                : (payload?.subsidiary?.id ?? "\u2014")
          }
        />
        {onChange ? (
          <PreviewField label="Transaction Date">
            <PreviewInput
              type="date"
              value={payload?.trandate ?? ""}
              onChange={(e) => onChange({ trandate: e.target.value })}
              className="text-sm font-medium text-palette-gray-text border border-palette-gray-border rounded px-2 py-1 bg-white focus:outline-none focus:ring-2 focus:ring-palette-indigo-label"
            />
          </PreviewField>
        ) : (
          <PreviewField label="Transaction Date" value={payload?.trandate} />
        )}
        <PreviewField label="Practitioners" value={clinicRows?.length ?? 0} />
        <div className="sm:col-span-2">
          {onChange ? (
            <PreviewField label="Memo">
              <PreviewInput
                type="text"
                value={payload?.memo ?? ""}
                onChange={(e) => onChange({ memo: e.target.value })}
                className="text-sm font-medium text-palette-gray-text border border-palette-gray-border rounded px-2 py-1 bg-white focus:outline-none focus:ring-2 focus:ring-palette-indigo-label"
              />
            </PreviewField>
          ) : (
            <PreviewField label="Memo" value={payload?.memo} />
          )}
        </div>
      </div>

      {/* Line items */}
      <div>
        <div className="mb-1 flex items-center justify-between">
          <span className="text-sm font-semibold text-palette-gray-heading">
            Line Items ({items.length})
          </span>
          <span
            className={`text-xs font-semibold ${balanced ? "text-palette-emerald-hover" : "text-palette-red-heading"}`}
          >
            {balanced ? "Balanced" : "Not balanced"}
          </span>
        </div>
        <div className="max-h-[28rem] overflow-y-auto rounded-md border border-palette-gray-divider">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-palette-gray-subtle text-palette-gray-heading">
              <tr>
                <th className="text-left px-3 py-3 font-semibold">Account</th>
                <th className="text-right px-3 py-3 font-semibold">Debit</th>
                <th className="text-right px-3 py-3 font-semibold">Credit</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 ? (
                <tr>
                  <td colSpan={3} className="px-3 py-4 text-center text-palette-gray-secondary">
                    No line items.
                  </td>
                </tr>
              ) : (
                items.map((it, idx) => {
                  const id = it.account?.id;
                  const name = id != null ? accountNameById[String(id)] : "";
                  const isDebit = Number(it.debit) > 0;
                  const isCredit = Number(it.credit) > 0;
                  const rowCls = isDebit
                    ? "bg-palette-emerald-soft hover:bg-palette-emerald-muted"
                    : isCredit
                      ? "bg-palette-rose-soft hover:bg-palette-rose-muted"
                      : "";
                  return (
                    <tr key={idx} className={`border-t border-palette-gray-subtle ${rowCls}`}>
                      <td className="px-3 py-3">
                        {name ? (
                          <span className="text-palette-gray-text">{name}</span>
                        ) : accountsLoading ? (
                          <span className="text-palette-gray-placeholder italic text-xs">
                            Loading…
                          </span>
                        ) : (
                          <span className="font-mono text-xs text-palette-gray-secondary">
                            {id ?? "—"}
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-3 text-right">
                        {onChange ? (
                          <input
                            type="number"
                            step="0.01"
                            value={it.debit ?? ""}
                            onChange={(e) => {
                              const raw = e.target.value;
                              const next = raw === "" ? null : Number(raw);
                              const newItems = items.map((row, i) =>
                                i === idx ? { ...row, debit: next } : row,
                              );
                              onChange({ line: { ...payload.line, items: newItems } });
                            }}
                            className="w-28 text-right text-sm font-medium text-palette-gray-text border border-palette-gray-border rounded px-2 py-1 bg-white focus:outline-none focus:ring-2 focus:ring-palette-indigo-label"
                          />
                        ) : it.debit != null ? (
                          fmtLineAmount(it.debit)
                        ) : (
                          ""
                        )}
                      </td>
                      <td className="px-3 py-3 text-right">
                        {onChange ? (
                          <input
                            type="number"
                            step="0.01"
                            value={it.credit ?? ""}
                            onChange={(e) => {
                              const raw = e.target.value;
                              const next = raw === "" ? null : Number(raw);
                              const newItems = items.map((row, i) =>
                                i === idx ? { ...row, credit: next } : row,
                              );
                              onChange({ line: { ...payload.line, items: newItems } });
                            }}
                            className="w-28 text-right text-sm font-medium text-palette-gray-text border border-palette-gray-border rounded px-2 py-1 bg-white focus:outline-none focus:ring-2 focus:ring-palette-indigo-label"
                          />
                        ) : it.credit != null ? (
                          fmtLineAmount(it.credit)
                        ) : (
                          ""
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
            <tfoot className="bg-palette-gray-soft">
              <tr className="border-t border-palette-gray-divider">
                <td className="px-3 py-3 text-right font-semibold">Totals</td>
                <td className="px-3 py-3 text-right font-semibold">{fmtLineAmount(totalDebit)}</td>
                <td className="px-3 py-3 text-right font-semibold">{fmtLineAmount(totalCredit)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      <p className="text-xs text-palette-gray-secondary">
        This will create a new Journal Entry in NetSuite. Please review the values above before
        confirming.
      </p>
    </div>
  );
}
